import ComposedChart, {
  ChartDataPoint,
  ChartSeries,
  TooltipContentType,
} from '@/components/Chart/ComposedChart';
import { Card } from '@/components/ui/card';
import { CHART_BAR_COLOR, CHART_LINE_COLOR } from '@/constants/chart';
import { formatIntegerCompact, formatIntegerFull } from '@/utils/numberUtil';
import { formatTooltipDate } from '@/utils/timeUtils';
import { BarChart3 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

interface TransactionsDataPoint {
  date: string;
  transactions: number;
  cumulativeTransactions: number;
}

interface TransactionsChartProps {
  data?: TransactionsDataPoint[];
  height?: number;
}

/**
 * Custom Tooltip for Transactions Chart
 */
const TransactionsTooltip: TooltipContentType = ({ active, payload }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0]?.payload;
  if (!data) return null;

  const daily = data.daily as number;
  const cumulative = data.cumulative as number;

  return (
    <div className="bg-solid-toast-tips rounded-lg p-3 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]">
      {/* Date */}
      <div className="text-xs font-semibold text-text-black leading-3 mb-3">
        {formatTooltipDate(data.date)}
      </div>

      {/* Cumulative and Daily values */}
      <div className="flex flex-col gap-2 text-xs text-text-black leading-3">
        <div>Cumulative Transactions: {formatIntegerFull(cumulative)}</div>
        <div>Daily Transactions: {formatIntegerFull(daily)}</div>
      </div>
    </div>
  );
};

/**
 * Transactions Chart Component
 * Displays daily transactions (bar) and cumulative transactions (line)
 */
const TransactionsChart: React.FC<TransactionsChartProps> = ({ data, height = 232 }) => {
  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>({
    daily: true,
    cumulative: true,
  });

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(item => ({
      date: item.date,
      daily: item.transactions,
      cumulative: item.cumulativeTransactions,
    })) as ChartDataPoint[];
  }, [data]);

  const allSeries: ChartSeries[] = [
    { dataKey: 'daily', name: 'Daily Transactions', type: 'bar', color: CHART_BAR_COLOR },
    {
      dataKey: 'cumulative',
      name: 'Cumulative Transactions',
      type: 'line',
      color: CHART_LINE_COLOR,
    },
  ];

  const series = allSeries.filter(s => visibleSeries[s.dataKey]);

  const legends = [
    { dataKey: 'daily', label: 'Daily Transactions', color: CHART_BAR_COLOR },
    { dataKey: 'cumulative', label: 'Cumulative Transactions', color: CHART_LINE_COLOR },
  ];

  const toggleSeries = (dataKey: string) => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  const hasData = chartData.length > 0;

  return (
    <Card className="overflow-hidden bg-solid-white-card">
      {/* Header */}
      <div className="p-6 pb-4">
        <h3 className="text-base font-semibold text-text-black h-6 my-0">Transactions</h3>
      </div>

      {/* Chart Area */}
      <div className="px-6 pb-4">
        {hasData ? (
          <ComposedChart
            data={chartData}
            series={series}
            height={height}
            yAxisFormatter={formatIntegerCompact}
            customTooltip={TransactionsTooltip}
          />
        ) : (
          <div
            className="flex items-center justify-center border-2 border-dashed border-border-light-line-1 rounded-lg bg-solid-solid2"
            style={{ height: `${height}px` }}
          >
            <div className="text-center">
              <BarChart3 className="h-12 w-12 text-icon-grey mx-auto mb-3" />
              <p className="text-sm text-text-main-body">No Data Available</p>
              <p className="text-xs text-text-prompt mt-1">Transactions</p>
            </div>
          </div>
        )}
      </div>

      {/* Legends */}
      <div className="px-6 pb-4 flex items-center justify-center gap-6">
        {legends.map((legend, index) => (
          <div
            key={index}
            className="flex items-center gap-2 cursor-pointer select-none transition-opacity hover:opacity-80"
            onClick={() => toggleSeries(legend.dataKey)}
            style={{ opacity: visibleSeries[legend.dataKey] ? 1 : 0.4 }}
          >
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: legend.color }} />
            <span className="text-sm text-text-main-body">{legend.label}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default TransactionsChart;
